import type { WritableDraft } from "immer";
import type { TemporalState } from "zundo";

import { t } from "@lingui/core/macro";
import { debounce } from "es-toolkit";
import isDeepEqual from "fast-deep-equal";
import { current } from "immer";
import { toast } from "sonner";
import { temporal } from "zundo";
import { immer } from "zustand/middleware/immer";
import { create } from "zustand/react";
import { useStoreWithEqualityFn } from "zustand/traditional";

import type { StoredPaginationPreference } from "@/integrations/multiresume/pagination/preferences";
import type { ResumeData } from "@/schema/resume/data";

import { orpc, type RouterOutput } from "@/integrations/orpc/client";

type Resume = Pick<
  RouterOutput["resume"]["getByIdForPrinter"],
  "id" | "name" | "slug" | "tags" | "data" | "isLocked"
> & {
  paginationPreference?: StoredPaginationPreference | null;
};

type ResumeStoreState = {
  resume: Resume;
  isReady: boolean;
};

type ResumeStoreActions = {
  initialize: (resume: Resume | null) => void;
  setPaginationPreference: (preference: StoredPaginationPreference | null) => void;
  replaceResumeDataFromServer: (data: ResumeData) => void;
  updateResumeData: (fn: (draft: WritableDraft<ResumeData>) => void) => void;
};

type ResumeStore = ResumeStoreState & ResumeStoreActions;

type ResumeSyncCacheInvalidatorOptions = {
  refetch?: boolean;
};

type ResumeSyncCacheInvalidator = (
  resumeId: string,
  options: ResumeSyncCacheInvalidatorOptions,
) => Promise<void> | void;

const controller = new AbortController();
const signal = controller.signal;

let syncErrorToastId: string | number | undefined;
let syncCacheInvalidator: ResumeSyncCacheInvalidator | null = null;
let latestSyncPromise: Promise<boolean> | null = null;

export const setResumeSyncCacheInvalidator = (invalidator: ResumeSyncCacheInvalidator | null) => {
  syncCacheInvalidator = invalidator;

  return () => {
    if (syncCacheInvalidator === invalidator) syncCacheInvalidator = null;
  };
};

const invalidateResumeSyncCache = async (resumeId: string, options: ResumeSyncCacheInvalidatorOptions) => {
  try {
    await syncCacheInvalidator?.(resumeId, options);
  } catch (error) {
    console.warn("[resume autosave] failed to refresh resume query caches", error);
  }
};

const _syncResume = (resume: Resume) => {
  const syncPromise = (async () => {
    try {
      await orpc.resume.update.call({ id: resume.id, data: resume.data }, { signal });
      await invalidateResumeSyncCache(resume.id, { refetch: false });

      // Dismiss error toast on successful sync
      if (syncErrorToastId !== undefined) {
        toast.dismiss(syncErrorToastId);
        syncErrorToastId = undefined;
      }

      return true;
    } catch (error: unknown) {
      // Ignore aborted requests (e.g. page navigation)
      if (error instanceof DOMException && error.name === "AbortError") return true;

      syncErrorToastId = toast.error(
        t`Your latest changes could not be saved. Please make sure you are connected to the internet and try again.`,
        { id: syncErrorToastId, duration: Infinity },
      );

      return false;
    }
  })();

  latestSyncPromise = syncPromise;
  void syncPromise.finally(() => {
    if (latestSyncPromise === syncPromise) latestSyncPromise = null;
  });
};

const syncResume = debounce(_syncResume, 500, { signal });

export const flushResumeSync = async (options: ResumeSyncCacheInvalidatorOptions = {}) => {
  syncResume.flush();

  const pendingSync = latestSyncPromise;
  const didSync = pendingSync ? await pendingSync : true;
  const resumeId = useResumeStore.getState().resume?.id;

  if (didSync && options.refetch === true && resumeId) {
    await invalidateResumeSyncCache(resumeId, { refetch: true });
  }

  return didSync;
};

// Flush pending sync before the page unloads to prevent data loss
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    syncResume.flush();
  });
}

let errorToastId: string | number | undefined;

type PartializedState = { resume: Resume | null };

export const useResumeStore = create<ResumeStore>()(
  temporal(
    immer((set) => ({
      resume: null as unknown as Resume,
      isReady: false,

      initialize: (resume) => {
        set((state) => {
          state.resume = resume as Resume;
          state.isReady = resume !== null;
          useResumeStore.temporal.getState().clear();
        });
      },

      setPaginationPreference: (preference) => {
        set((state) => {
          if (!state.resume) return state;
          state.resume.paginationPreference = preference;
        });
      },

      replaceResumeDataFromServer: (data) => {
        set((state) => {
          if (!state.resume) return state;
          state.resume.data = structuredClone(data) as WritableDraft<ResumeData>;
        });
      },

      updateResumeData: (fn) => {
        set((state) => {
          if (!state.resume) return state;

          if (state.resume.isLocked) {
            errorToastId = toast.error(t`This resume is locked and cannot be updated.`, { id: errorToastId });
            return state;
          }

          fn(state.resume.data);
          syncResume(current(state.resume));
        });
      },
    })),
    {
      partialize: (state) => ({ resume: state.resume }),
      equality: (pastState, currentState) => isDeepEqual(pastState, currentState),
      limit: 100,
    },
  ),
);

export function useTemporalStore<T>(selector: (state: TemporalState<PartializedState>) => T): T {
  return useStoreWithEqualityFn(useResumeStore.temporal, selector);
}
